<script module>
  export const i18n = {
    sv: {
      'Show more passes': 'Visa fler pass',
      'Something went wrong.': 'Något gick fel.',
      'Please ensure that everything is filled in correctly and try again.':
        'Kontrollera att all information är korrekt och försök igen.'
    },
    da: {
      'Show more passes': 'Vis flere pas',
      'Something went wrong.': 'Noget gik galt.',
      'Please ensure that everything is filled in correctly and try again.':
        'Kontroller, at alt er korrekt indtastet og prøv igen.'
    }
  }
</script>

<script>
  import { enhance, applyAction } from '$app/forms'
  import { addDays, addYears } from 'date-fns'
  import { goto } from '$app/navigation'
  import { page } from '$app/state'

  import { WITHOUT_DECIMALS, money } from '$lib/utils/money.svelte.js'
  import { passengersToParams } from '$lib/utils/passengers.js'
  import { i18n as countryNames } from '$lib/countries.js'
  import { register } from '$lib/utils/text.svelte.js'
  import { asDatestamp } from '$lib/Datepicker.svelte'
  import PassBundles from '$lib/PassBundles.svelte'
  import { track } from '$lib/plausible.svelte.js'
  import { gql } from '$lib/graphql.svelte.js'
  import State from '$lib/utils/state.svelte'
  import Button from '$lib/Button.svelte'
  import PassBundleBuilder, {
    getValidityKey,
    i18n as builderI18n,
    PassBundlesFragment
  } from '$lib/PassBundleBuilder.svelte'

  /**
   * Get the earliest possible departure date based on the passenger attributes
   * @param {Schema.PassPassengerInput[]} passengers
   */
  const getProbableDepartureDate = (passengers) => {
    if (
      passengers.every(
        (passenger) => passenger.type === 'ADULT' || passenger.age != null
      )
    ) {
      return undefined
    }

    const date = passengers.reduce((date, passenger) => {
      const { birthDate, age } = passenger
      return birthDate && age != null
        ? Math.max(date, +addYears(new Date(birthDate), age))
        : date
    }, +addDays(new Date(), 40))

    return asDatestamp(new Date(date))
  }

  /**
   * Sanitize the passengers array to ensure that the birthDate and age are not null
   * @param {Schema.PassPassengerInput[]} passengers
   * @returns {Schema.PassPassengerInput[]}
   */
  const sanitizePassengers = (passengers) => {
    return passengers.map((passenger) => {
      const { birthDate, age, type } = passenger
      return { birthDate: birthDate ?? undefined, age: age ?? undefined, type }
    })
  }

  let text = register(i18n, builderI18n, countryNames)

  /** @type {{
    id: string,
    product: string,
    badge?: string,
    campaignCode?: string,
    departureDate?: string,
    expanded?: boolean,
    position?: 'top' | 'center',
    forwardAgentApiKey?: boolean,
    passengers?: Schema.PassPassengerInput[],
    passengerInputMode?: 'age' | 'birthDate',
    PassBundlesComponent?: Svelte.Component<{
      bundles?: Schema.PassBundle[],
      badge?: string,
      loading?: boolean,
      onselect?: function(Schema.PassBundle): void,
      label?: Svelte.Snippet<[Schema.PassBundle]>,
      price?: Svelte.Snippet<[Schema.PassBundle]>
    }>,
    children?: Svelte.Snippet
  }} */
  let {
    id,
    product,
    badge,
    campaignCode,
    departureDate,
    expanded = false,
    passengerInputMode,
    position = 'center',
    forwardAgentApiKey = false,
    PassBundlesComponent = PassBundles,
    passengers = [{ type: 'YOUTH', age: 18 }],
    children
  } = $props()

  const initialParams = Object.fromEntries(page.url.searchParams)
  let isExpanded = $state(expanded || `${id}-expanded` in initialParams)
  let boundPassengers = $state(passengers)

  let loading = $state(false)
  const onbook = () => {
    loading = true
    return async ({ result }) => {
      track('Begin Checkout', {
        props: {
          campaignCode,
          product
        }
      })

      try {
        if (result.type === 'redirect') {
          const referrer = page.url.href.replace(page.url.origin, '')
          goto(result.location, { state: { referrer } })
        } else {
          track('Exception', {
            props: { message: result.data?.message, ...result.data?.extra }
          })
          await applyAction(result)
        }
      } finally {
        if (result.type !== 'redirect' || !result.location.startsWith('/')) {
          loading = false
        }
      }
    }
  }

  /** @type {App.GraphQLOperation<{ bundles: Schema.PassBundle[] }, { passengers: Schema.PassPassengerInput[], currency: string, filter: Schema.PassBundleFilterInput, departureDate?: string }>} */
  const GetPassBundles = gql`
    query GetPassBundles(
      $passengers: [PassPassengerInput!]!
      $filter: PassBundleFilterInput
      $departureDate: Date
      $currency: String
    ) {
      bundles: getPassBundles(
        passengers: $passengers
        currency: $currency
        filter: $filter
        departureDate: $departureDate
      ) {
        ...${PassBundlesFragment}
      }
    }
  `

  const GetFullPricePassBundles = GetPassBundles.create()

  $effect(() =>
    GetPassBundles.execute({
      currency: State.currency,
      passengers: sanitizePassengers(passengers),
      filter: { product, campaignCode, class: '2ndclass' },
      departureDate: departureDate ?? getProbableDepartureDate(passengers)
    })
  )

  $effect(() => {
    if (campaignCode) {
      return GetFullPricePassBundles.execute({
        currency: State.currency,
        filter: { product, class: '2ndclass' },
        passengers: sanitizePassengers(passengers),
        departureDate: departureDate ?? getProbableDepartureDate(passengers)
      })
    }
  })

  /** @type {Schema.PassBundle|undefined} */
  let placeholder = $derived(
    GetPassBundles?.data?.bundles.find(
      (bundle) =>
        getValidityKey(bundle) === page.url.searchParams.get('validity')
    )
  )

  const onselect = (bundle) => {
    placeholder = bundle

    track('Select Rail Pass', {
      props: {
        campaignCode,
        product
      }
    })

    const href = withParams({
      validity: getValidityKey(bundle),
      product: bundle.product,
      class: '2ndclass',
      id
    })

    goto(href, { noScroll: true })
  }

  const withParams = (params) => {
    const query = new URLSearchParams(page.url.searchParams)
    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        query.set(key, value[0])
        for (const item of value.slice(1)) {
          query.append(key, item)
        }
      } else if (value != null) {
        query.set(key, value)
      }
    }
    return `${page.url.pathname || ''}?${query}`
  }
</script>

<div class="pass-bundle-shop" {id}>
  <form class="bundles" onsubmit={(event) => event.preventDefault()}>
    <PassBundlesComponent
      {badge}
      {onselect}
      loading={GetPassBundles.fetching ||
        (!GetPassBundles.data && !GetPassBundles.error)}
      bundles={GetPassBundles.data?.bundles
        .slice()
        .sort((a, b) => a.price.amount - b.price.amount)
        .slice(0, isExpanded ? GetPassBundles.data?.bundles.length : 4)}>
      {#snippet price(bundle)}
        <strong>{money(bundle.price, WITHOUT_DECIMALS)}</strong>
        {#if GetFullPricePassBundles?.data}
          {@const key = getValidityKey(bundle)}
          {@const fullPriceBundle = GetFullPricePassBundles.data.bundles.find(
            (fullPriceBundle) => getValidityKey(fullPriceBundle) === key
          )}
          {#if fullPriceBundle}
            <s class="u-textMuted">
              {money(fullPriceBundle.price, WITHOUT_DECIMALS)}
            </s>
          {/if}
        {/if}
      {/snippet}
    </PassBundlesComponent>
  </form>

  {#if !isExpanded && GetPassBundles.data && GetPassBundles.data?.bundles.length > 4}
    <Button
      muted
      href="{withParams({ [`${id}-expanded`]: '' })}#{id}"
      onclick={(event) => {
        isExpanded = true
        event.preventDefault()
      }}>
      {text`Show more passes`}
    </Button>
  {/if}

  {#if placeholder}
    {#snippet error()}
      <strong>
        {text`Something went wrong.`}
      </strong>
      {text`Please ensure that everything is filled in correctly and try again.`}
    {/snippet}
    <form
      method="POST"
      class="builder"
      use:enhance={onbook}
      action="{State.prefix}/booking{forwardAgentApiKey
        ? `?publicApiKey=${page.data.agentApiKey}`
        : ''}">
      <input type="hidden" name="currency" value={State.currency} />
      <PassBundleBuilder
        {loading}
        {position}
        {placeholder}
        {departureDate}
        {passengerInputMode}
        currency={State.currency}
        bind:passengers={boundPassengers}
        filter={{ campaignCode, product }}
        error={page.status === 500 && page.form?.message ? error : undefined}
        onclose={() => {
          placeholder = undefined
          goto(page.url.pathname, { replaceState: true, noScroll: true })
        }}>
        {@render children?.()}
      </PassBundleBuilder>
      {#each passengersToParams(boundPassengers) as value}
        <input type="hidden" name="passenger" {value} />
      {/each}
    </form>
  {/if}
</div>

<style>
  .pass-bundle-shop {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    width: 100%;
  }

  .bundles {
    width: 100%;
  }

  .builder {
    display: contents;
  }
</style>
